/* ══════════════════════════════════════════════════════════════════
   DESIGN TOOL — CART FEE/PROOF SYNC
   Keeps fee + proof line items in lockstep with the design they belong to.

   @version 1.4.3  (Brandon, Aug 28 2026) — $5 PROOF VARIANT ID UPDATED
     PROOF_VARIANT_ID 46207629459628 → 47088548741292 (the proof product's variants were
     rebuilt when the $0.00 required-proof variant was added for the NO-PROOF window;
     design-tool.liquid proofVariantId updated to match). No logic change. This is the
     PARKED proof version — see CB Design Tool/PARKED-PROOF-OPTION-README.md.
   Prior 1.4.2  (Brandon, Jul 30 2026) — PROOF RE-ADD (Rule 6, add half)
     Fixes a reported missing proof fee on a Zakeke line: the customer checked
     the in-cart proof box (proof line added, _artist_proof='Yes' stamped),
     but the proof line was later dropped — a Zakeke edit round-trip removes
     and re-adds the main, and a reconcile in that window cascades the proof
     out (rule 3) — and nothing re-added it. Reconcile now treats
     _artist_proof==='Yes' on a design line as durable intent: if its proof
     line is missing, re-add it (both customizers). Reproduced + confirmed on
     the live cart Jul 30 2026 (deleted the proof line, reloaded, saw
     _artist_proof='Yes' with no proof and no re-add). Self-heals carts already
     stuck in the checked-but-no-fee state on next cart load. ADD only —
     removal stays with the toggle. NOTE: the ZK- ref prefix was investigated
     and is NOT the cause; it is used consistently in cart-sync AND
     main-cart-items.liquid, and removing it would orphan existing
     _upcharge_for/_proof_for lines (they carry 'ZK-<id>'), so it was left as-is.
   Prior 1.4.1  (Brandon, Jul 30 2026) — ZAKEKE PRE-CHECKOUT KILL-SWITCH HERE TOO
     window.zakekeUpdateCartPreCheckout is now ALSO defined by this script
     (guarded — never overwrites an existing definition). The theme snippet
     zakeke-customizer-price.liquid v2.1 already defines it on every themed
     page; duplicating it here covers any layout that loads cart-sync
     without the snippet, and any future snippet edit/revert. Zakeke's
     hosted product.js calls this hook INSTEAD of its pre-checkout
     draft-order pass, which (a) crashes off the product page — its map
     dereferences zakekePricingData.product.variants[0], product-page-only —
     and (b) would double-charge beside our fee line. Promise.resolve(null)
     is Zakeke's own "nothing extra to charge" branch.
     NOTE this cannot fix pages loaded BEFORE the v2.1 snippet deploy (Jul 29
     2026 ~4pm ET) — a stale tab runs the HTML it loaded; those heal on
     refresh. Fresh-page checkout with cp=600 + fee line was verified
     end-to-end on the live store Jul 30 2026.
   Prior 1.4.0  (Brandon, Jul 29 2026) — SYMMETRIC ZAKEKE FEE, MARKER GONE
     The Zakeke extra-side fee is now a pure FUNCTION of the design line's
     properties._configurationPrice, in both directions:
       cp matches a customization-price-1 tier → exactly one tagged fee line
       cp is 0 / absent                        → no tagged fee line
       cp positive, matches no tier            → log, touch nothing
     Two live defects closed:
     - REVENUE LEAK (reported): _dt_fee_added: 'Yes' was stamped when the fee
       was ADDED, and Zakeke's edit round-trip replaces the cart line and
       copies its properties verbatim, so the stamp rode along. Once a design
       had ever carried a fee, editing it and re-adding a second side left
       _configurationPrice: '600' with no fee line — permanently, refresh or
       not. The stamp cannot tell "customer deleted it" from "Zakeke rebuilt
       the line", so it is deleted, not patched. The Zakeke fee is now
       mandatory — nobody ends up fighting the reconciler because
       main-cart-items.liquid ALREADY renders _upcharge_for rows with a
       read-only quantity and no remove button (dt_is_addon, unchanged in
       this release; verified against the live cart Jul 29 2026).
     - OVERCHARGE (found while fixing it): nothing removed a fee line while
       its main survived — the deps loop never computed want=0 for a live
       main. A 2-sided design edited down to 1 side kept its fee whenever
       Zakeke's remove+re-add both landed inside the 250ms debounce. The drop
       half of Rule 5 makes that deterministic instead of race-dependent.
     Also: explicit cp > 0 gate before the variant lookup (a future $0.00 tier
     on customization-price-1 can never attach zero-dollar lines), and
     _dt_fee_added joins _configurationVariants in the legacy strip pass.
     Rules 1-4, the fetch/XHR hooks, the strip mechanism and the v1.3.0
     checkout gate are untouched. Design-tool fees are untouched — Rule 5 has
     always been ZK- only.
   Prior 1.3.0  (Brandon, Jul 27 2026) — CHECKOUT GATE
     Closes the last race left by 1.2.0: a customer clicking checkout within
     ~1s of first cart landing could beat the reconcile and check out without
     a just-needed fee line. A document-level CAPTURE listener now intercepts
     checkout clicks/submits while the cart is DIRTY (reconcile pending or in
     flight), waits for the pass to settle, then re-fires the original action.
     - Delegated at document level → covers checkout buttons rendered AFTER
       DOMContentLoaded (the hole the old Zakeke interceptor had).
     - Hard cap window.dtCheckoutHoldMaxMs (default 4000ms) and settle-on-
       error: a failed reconcile must NEVER block a sale — worst case the
       order goes through fee-less and production review flags it.
     - While a checkout is held, the cart-page reload after changes is
       skipped (navigation to checkout is imminent anyway).
     - Not coverable: wallet buttons living inside iframes (Shop Pay /
       Apple Pay sheets). The load-time pass typically settles before those
       are reachable.
   Prior 1.2.0  (Brandon, Jul 27 2026) — SINGLE FEE OWNER
     Root cause of missing second-side fees found in the zakeke-customizer-price
     asset: its reconciler ZEROED every cart line whose variant id appeared in
     any _configurationVariants — including OUR tagged fee lines — on every
     /cart page view, then relied on a checkout-button click handler (which
     misses express/dynamic/drawer buttons) to re-add a bare fee at the last
     second. Our _dt_fee_added marker then read that deletion as "customer
     removed it on purpose" and blocked the re-add. Fix = single ownership:
     the asset was rewritten (v2.0) to stop stamping _configurationVariants
     and its reconciler deleted; THIS script is now the only fee manager.
     - Rule 5 generalized (Brandon's choice, Jul 27): fee variant resolved
       from window.dtZakekeFeeVariants (published by the rewritten asset from
       customization-price-1's variant list) — any _configurationPrice that
       matches a variant's price is charged. Hardcoded 2000/600 pairs remain
       as backstop when the list is absent. Unmatched values: log + leave.
     - Legacy guard: if the cart still holds a BARE fee-variant line (added
       by the old Zakeke checkout handler, no _upcharge_for), Rule 5 adds are
       skipped that pass — never double-charge a straddling cart.
     - Legacy cleanup: _configurationVariants is stripped from cart lines so
       STALE TABS still running the old asset code find nothing to zero.
   Prior 1.1.0 (Jul 23 2026): #3 marker stamped on every line sharing a ref;
     #4 pageshow (bfcache) reconcile pass; load EARLY in <head> before Zakeke.
   Prior 1.0.0 (Jul 17 2026): Rule 5 (Zakeke extra-side fees) added.

   Contract (set by design-tool-core.js executeCartAdd):
     main item :  properties._design_serial = SERIAL   (no _upcharge_for / _proof_for)
     fee item  :  properties._upcharge_for  = SERIAL   (qty mirrors the main item)
     proof item:  properties._proof_for     = SERIAL   (qty is ALWAYS 1 — one proof
                                                        per design, per Brandon 2026-07)

   Rules enforced after every cart mutation:
     1. fee.qty   === main.qty   (covers qty bumps on either line — main wins)
     2. proof.qty === 1
     3. main item gone → its fee + proof lines are removed
     4. $1000 proof rule tracks in-cart qty edits: when a line's total
        (unit price + its fee units, x qty) crosses ABOVE $1000 the proof
        becomes required — proof line added, _artist_proof=Yes and the
        _proof_required note stamped (liquid then locks the toggle). When
        it drops back to $1000 or below, the note is removed so the toggle
        unlocks; the proof line itself stays until the customer removes it.
     5. Zakeke extra-side fees (Brandon, Jul 17 2026; made symmetric and
        unconditional Jul 29 2026). Zakeke stamps properties._configurationPrice
        (cents, as a STRING) on its line: the matching customization-price-1
        variant's price when more than one side is customized, and "0" when
        it is not. cart-sync holds the cart to that number both ways —
        a matching tier means exactly one fee line tagged
        _upcharge_for = 'ZK-<customization>' (rules 1-3 then keep qty
        mirrored and cascade removal exactly like design-tool fees), and
        0/absent means no such line survives. EXACT price match only: any
        other POSITIVE value is console-logged and nothing is touched —
        never guess a charge, never silently strip one. There is no
        opt-out marker; see the v1.4.0 note above for why.
   Scoped strictly per serial: a change to one design never touches another
   design's fee/proof lines.

   Deploy: upload as asset `design-tool-cart-sync.min.js` and include it ONCE,
   as EARLY as possible in <head> and BEFORE any Zakeke script — NOT deferred:
     <script src="{{ 'design-tool-cart-sync.min.js' | asset_url }}"></script>
   Why early + synchronous: this IIFE wraps window.fetch + XHR the instant it
   runs. If Zakeke's bundle executes first and captures its own fetch ref, its
   add-to-cart bypasses the hook and we'd rely solely on the load-time pass.
   Running first wins that race. The script does almost nothing synchronously
   (wrap + one listener; reconcile is debounced/async), so a <head> include is
   effectively free and safe (it handles document.readyState itself).

   Implementation notes:
   - Theme-agnostic: intercepts fetch + XHR to /cart/change, /cart/update,
     /cart/add, /cart/clear rather than binding to theme DOM. Non-AJAX cart
     form posts are covered by the reconcile-on-page-load / pageshow passes.
   - Our own reconcile calls use the captured pre-patch fetch, so they can't
     re-trigger the hook (no loops). A reconcile that changes nothing ends the
     cycle, so the load-time pass can't reload-loop either.
   - Line items are addressed by line `key` (variant id + properties hash), so
     two designs sharing the same fee variant stay independent.
   - If the customer deletes ONLY a fee/proof line while keeping the product,
     we do NOT re-add it (the variant/config knowledge lives in the tool, not
     here). Production should treat a design item without its fee line as an
     order-review flag.
   ══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  if (window.__dtCartSyncInstalled) return;
  window.__dtCartSyncInstalled = true;

  /* Zakeke pre-checkout kill-switch (v1.4.1) — see the header note. The
     snippet's definition wins if it ran first; this one is the backstop. */
  if (!window.zakekeUpdateCartPreCheckout) {
    window.zakekeUpdateCartPreCheckout = function () { return Promise.resolve(null); };
  }

  var origFetch = window.fetch.bind(window);
  var DEBUG = /[?&]dt_debug/.test(location.search);
  var log = function () { if (DEBUG) console.log.apply(console, ['[dt-cart-sync]'].concat([].slice.call(arguments))); };

  var MUTATION_RE = /\/cart\/(change|update|add|clear)(\.js)?(\?|$)/;

  /* ── Reconcile engine ─────────────────────────────────────────── */
  var pending = null;      /* debounce timer */
  var running = false;     /* a reconcile pass is in flight */
  var rerun = false;       /* a mutation landed mid-pass — run again */

  function scheduleReconcile(reason) {
    log('mutation seen:', reason);
    markDirty(); /* checkout gate holds until this pass settles */
    if (running) { rerun = true; return; }
    clearTimeout(pending);
    pending = setTimeout(runReconcile, 250);
  }

  function runReconcile() {
    running = true;
    reconcile().catch(function (e) {
      log('reconcile error', e);
    }).then(function () {
      running = false;
      if (rerun) { rerun = false; scheduleReconcile('rerun'); }
      else { markSettled(); } /* settles on error too — never hold checkout hostage */
    });
  }

  function getCart() {
    return origFetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('cart.js ' + r.status); return r.json(); });
  }

  function changeLine(key, qty) {
    return origFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { if (!r.ok) throw new Error('change.js ' + r.status); return r.json(); });
  }

  function reconcile() {
    return getCart().then(function (cart) {
      var mains = {};   /* serial → main item */
      var deps = [];    /* fee/proof items with their target rule */

      cart.items.forEach(function (it) {
        var p = it.properties || {};
        if (p._upcharge_for) { deps.push({ item: it, serial: p._upcharge_for, kind: 'fee' }); return; }
        if (p._proof_for)    { deps.push({ item: it, serial: p._proof_for,    kind: 'proof' }); return; }
        if (p._design_serial) { mains[p._design_serial] = it; return; }
        /* Zakeke customizer lines have no design serial — key them by their
           customization id so in-cart proofs (ref 'ZK-<id>') get the same
           qty/cascade rules as design-tool proofs. */
        if (p.customization) mains['ZK-' + p.customization] = it;
      });

      /* Quantity per ref — two Zakeke lines CAN share a customization id
         (same saved design in two variants); the mains map keeps only the
         last, so fee quantities sum across all lines with the ref. Design
         serials are unique by construction (sum = the one line's qty). */
      var qtyByRef = {};
      cart.items.forEach(function (it) {
        var p = it.properties || {};
        var ref = p._design_serial || (p.customization ? 'ZK-' + p.customization : null);
        if (ref && !p._upcharge_for && !p._proof_for) qtyByRef[ref] = (qtyByRef[ref] || 0) + it.quantity;
      });

      /* ── Rule 5 (v1.4.0) — decided BEFORE the quantity pass, because the
         drop half below has to be able to zero a fee line through the same
         `changes` list rather than fighting it.

           cp matches a customization-price-1 tier → exactly one tagged line
           cp is 0 / absent                        → no tagged line survives
           cp positive but matches no tier         → log, touch nothing

         MEASURED Jul 29 2026 on the live cart: removing a side in Zakeke's
         editor rewrites _configurationPrice to the STRING "0" — it is not
         dropped and never left stale, and Zakeke's own pre-checkout pass
         special-cases 0 === parseInt(cp) identically. Note that _previews is
         NOT a side count: it lists every product side, decorated or not (a
         one-sided design still carries a #Back entry), so _configurationPrice
         is the only authoritative signal.

         Legacy guard (v1.2.0, kept): the OLD zakeke asset's checkout handler
         added BARE fee-variant lines (no _upcharge_for). A cart straddling
         that deploy can still hold one, and adding our tagged fee beside it
         would double-charge — so ADDS are skipped while a bare line remains
         (it ages out with the cart). DROPS are deliberately not guarded:
         zeroing a line we tagged ourselves can never double-charge. */
      var bareFeeLine = null;
      (function () {
        var feeIds = knownFeeVariantIds();
        for (var bi = 0; bi < cart.items.length; bi++) {
          var bq = cart.items[bi].properties || {};
          if (!bq._upcharge_for && !bq._proof_for && feeIds.indexOf(cart.items[bi].id) !== -1) {
            bareFeeLine = cart.items[bi]; return;
          }
        }
      })();
      var zkFeeOps = [];    /* fee lines to ADD */
      var zkDropRefs = {};  /* ref → true: nothing chargeable, any fee line goes */
      Object.keys(mains).forEach(function (ref) {
        if (ref.indexOf('ZK-') !== 0) return;
        var main = mains[ref];
        var p = main.properties || {};
        var rawCp = p._configurationPrice;
        var absent = (rawCp == null || rawCp === '');
        /* Number(), not parseInt — '600.5'/'600abc' must NOT pass as 600. */
        var cp = absent ? 0 : Number(rawCp);

        if (!absent && !isFinite(cp)) {
          /* Garbage, not a statement that the configuration is free. Adding
             and dropping are BOTH guesses here, so do neither. */
          log('unreadable _configurationPrice', JSON.stringify(rawCp), 'on', ref, '— left alone');
          return;
        }
        if (cp < 0) {
          log('negative _configurationPrice', JSON.stringify(rawCp), 'on', ref, '— left alone');
          return;
        }
        if (cp === 0) {
          /* Zakeke says this configuration costs nothing extra: "0" on a
             one-sided design, or the property never written. Any fee line
             still tagged to this ref goes. Testing === 0 BEFORE the variant
             lookup also means a $0.00 tier added to customization-price-1
             some day can never attach zero-dollar lines to every design. */
          zkDropRefs[ref] = true;
          return;
        }
        var variantId = feeVariantForCents(cp);
        if (!variantId) {
          log('unsupported _configurationPrice', JSON.stringify(rawCp), 'on', ref, '— no customization-price variant matches; left alone');
          return;
        }
        var hasFee = deps.some(function (d) { return d.serial === ref && d.kind === 'fee'; });
        if (hasFee) return;
        if (bareFeeLine) {
          log('legacy bare fee line in cart (variant ' + bareFeeLine.id + ') — skipping Rule 5 add for', ref, 'to avoid double-charge');
          return;
        }
        zkFeeOps.push({ ref: ref, variantId: variantId, qty: qtyByRef[ref] || main.quantity, cents: cp });
      });

      /* Compute the quantity changes this pass needs. Sequential execution:
         Shopify's cart endpoints reject concurrent mutations, and each change
         reindexes lines — keys stay stable, so key-addressed changes are safe. */
      var changes = [];
      deps.forEach(function (d) {
        var main = mains[d.serial];
        var want;
        if (!main) want = 0;                                         /* rule 3 — main is gone */
        else if (d.kind === 'fee' && zkDropRefs[d.serial]) want = 0;  /* rule 5 — drop half */
        else want = (d.kind === 'fee' ? (qtyByRef[d.serial] || main.quantity) : 1);
        if (d.item.quantity !== want) {
          changes.push({ key: d.item.key, qty: want, why: d.kind + ' for ' + d.serial + ' → ' + want });
        }
      });

      /* Rule 4 — $1000 proof requirement tracks in-cart qty edits.
         Total mirrors the design tool's add-to-cart formula: (unit price +
         its fee units) x the MAIN line's qty, pre-discount (original_price),
         proof fee excluded. Crossing above -> require (idempotent: the
         stamped note ends the cycle). Dropping to/below -> remove the note
         so liquid unlocks the toggle; the proof line stays unless the
         customer removes it. */
      var proofOps = [];
      Object.keys(mains).forEach(function (ref) {
        var main = mains[ref];
        var p = main.properties || {};
        var feeUnitCents = 0;
        deps.forEach(function (d) {
          if (d.serial === ref && d.kind === 'fee') {
            feeUnitCents += (d.item.original_price != null ? d.item.original_price : d.item.price) || 0;
          }
        });
        var unitCents = (main.original_price != null ? main.original_price : main.price) || 0;
        var over = ((unitCents + feeUnitCents) * main.quantity) / 100 > 1000;
        if (over && !p._proof_required) {
          proofOps.push({ ref: ref, key: main.key, mode: 'require' });
        } else if (!over && p._proof_required) {
          proofOps.push({ ref: ref, key: main.key, mode: 'release' });
        }
      });

      /* Rule 6 (v1.4.2) — proof line follows the customer's recorded choice.
         The product line's _artist_proof is the DURABLE intent ('Yes' when the
         customer checked the in-cart box or a forced condition set it). The
         proof line binds to its design by _proof_for = ref, exactly like the
         fee's _upcharge_for. If the intent is 'Yes' but the proof line is gone
         — dropped by a Zakeke edit round-trip (remove-before-add cascades the
         proof out via rule 3) or any cart hiccup — re-add it. Without this the
         box renders checked (liquid reads _artist_proof=='Yes') while no $5
         line exists: the customer thinks they're getting a proof and the order
         ships with no proof fee (reported Jul 2026, Zakeke line). This is the
         proof twin of Rule 5, and it covers BOTH customizers (serial and ZK-
         refs). ADD only: removal stays with the toggle (OFF sets _artist_proof
         ='No', which simply produces no add here). addProofLine is idempotent,
         so it never doubles and converges after one pass. */
      var proofAddOps = [];
      Object.keys(mains).forEach(function (ref) {
        var p = mains[ref].properties || {};
        if (p._artist_proof !== 'Yes') return;
        var hasProof = deps.some(function (d) { return d.serial === ref && d.kind === 'proof'; });
        if (!hasProof) proofAddOps.push({ ref: ref });
      });

      /* Legacy cleanup — strip dead properties off cart lines:
           _configurationVariants (v1.2.0): the old zakeke-customizer-price
             asset zeroed any cart line whose variant id appeared in it
             (deleting our fee lines). The rewritten asset no longer stamps
             it, but a STALE TAB can still run the old code — an empty
             property list starves it of targets.
           _dt_fee_added (v1.4.0): the retired no-re-add marker. Nothing
             reads it any more, and leaving it on lines would carry a
             meaningless flag onto orders and confuse production review.
         Runs LAST in the chain: a strip re-keys the line, and the qty
         `changes` above address lines by their captured key. */
      var stripOps = [];
      cart.items.forEach(function (it) {
        var q = it.properties || {};
        var drop = LEGACY_PROPS.filter(function (k) { return k in q; });
        if (!drop.length) return;
        stripOps.push({ key: it.key, props: drop, ref: q._design_serial || (q.customization ? 'ZK-' + q.customization : null) });
      });

      if (!changes.length && !proofOps.length && !zkFeeOps.length && !stripOps.length && !proofAddOps.length) { log('in sync — no changes'); return null; }

      var chain = Promise.resolve();
      changes.forEach(function (c) {
        chain = chain.then(function () { log('apply:', c.why); return changeLine(c.key, c.qty); });
      });
      zkFeeOps.forEach(function (op) {
        chain = chain.then(function () {
          log('adding Zakeke extra-side fee (' + op.cents + ') for', op.ref);
          /* No marker to stamp as of v1.4.0 — addFeeLine is idempotent and
             the next pass's hasFee check keeps this from ever doubling. */
          return addFeeLine(op.ref, op.variantId, op.qty);
        });
      });
      proofOps.forEach(function (op) {
        chain = chain.then(function () {
          if (op.mode === 'require') {
            log('proof now required (>$1000):', op.ref);
            /* Proof line first, then stamp — same self-healing order as the
               toggle (liquid treats proof-line presence as ON). */
            return addProofLine(op.ref).then(function () {
              /* Proof supersedes the review-checkbox approval — clear it so
                 the line never carries both contradictory flags. */
              return setLineProps(op.key, { _artist_proof: 'Yes', _proof_required: PROOF_REQUIRED_NOTE, _design_approved: null }, op.ref);
            });
          }
          log('proof requirement released (<=$1000):', op.ref);
          return setLineProps(op.key, { _proof_required: null }, op.ref);
        });
      });
      proofAddOps.forEach(function (op) {
        chain = chain.then(function () {
          log('proof chosen (_artist_proof=Yes) but line missing — re-adding for', op.ref);
          /* Idempotent: if the $1000 require branch above already added the
             proof for this ref in the same pass, addProofLine finds it and
             no-ops. */
          return addProofLine(op.ref);
        });
      });
      stripOps.forEach(function (op) {
        chain = chain.then(function () {
          log('stripping legacy', op.props.join(' + '), 'from', op.ref || op.key);
          /* Non-fatal: a failed strip just leaves the legacy prop for the
             next pass; nothing downstream depends on it. */
          var patch = {};
          op.props.forEach(function (k) { patch[k] = null; });
          return setLineProps(op.key, patch, op.ref)
            .catch(function (e) { log('strip failed (non-fatal):', e.message); });
        });
      });

      return chain.then(function () {
        /* Tell the theme the cart changed under it. The design tool already
           uses 'cart:refresh'; dispatch it plus a couple of common aliases,
           then hard-refresh the cart PAGE (drawer contexts get the events). */
        ['cart:refresh', 'cart:updated', 'cart-drawer:refresh'].forEach(function (ev) {
          document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
        });
        if (/^\/cart\/?$/.test(location.pathname)) {
          if (holdingCheckout) {
            /* v1.3.0: a held checkout resumes the moment this pass settles —
               navigation is imminent, a reload would only race it. */
            log('cart page — skipping reload, checkout is held');
          } else {
            log('cart page — reloading to render synced quantities');
            window.location.reload();
          }
        }
      });
    });
  }

  /* ── Mutation hooks ──────────────────────────────────────────── */
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = origFetch(input, init);
    if (MUTATION_RE.test(url)) {
      p.then(function () { scheduleReconcile('fetch ' + url); }, function () {});
    }
    return p;
  };

  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__dtCartUrl = url;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__dtCartUrl && MUTATION_RE.test(this.__dtCartUrl)) {
      this.addEventListener('loadend', function () { scheduleReconcile('xhr ' + this.__dtCartUrl); });
    }
    return origSend.apply(this, arguments);
  };

  /* ── Checkout gate (v1.3.0) ──────────────────────────────────────
     While the cart is DIRTY (a reconcile is pending or in flight), checkout
     clicks/submits are intercepted at the DOCUMENT level in the capture
     phase — delegation covers buttons the theme renders after
     DOMContentLoaded, which is exactly where the old Zakeke interceptor
     leaked. Once the pass settles (or the hard cap expires) the original
     action is re-fired through a bypass flag so every theme handler runs
     normally. The gate NEVER blocks a sale: it settles on reconcile errors
     and caps the hold at window.dtCheckoutHoldMaxMs (default 4000ms). */
  var dirty = false;             /* true from mutation-seen until pass settles */
  var settleWaiters = [];
  var holdingCheckout = false;   /* reconcile skips the cart-page reload while true */
  var gateBypass = false;        /* lets our own re-fired action through */

  function markDirty() { dirty = true; }
  function markSettled() {
    dirty = false;
    settleWaiters.splice(0).forEach(function (fn) { fn(); });
  }
  function whenSettled(maxMs) {
    return new Promise(function (resolve) {
      if (!dirty) return resolve(true);
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve(false); } }, maxMs);
      settleWaiters.push(function () { if (!done) { done = true; clearTimeout(t); resolve(true); } });
    });
  }

  /* Same control set the stock Zakeke snippet targeted (it was a sound
     selector — its failure was per-button binding at DOMContentLoaded). */
  var CHECKOUT_SEL = '[name=checkout], [href*=checkout]:not([href*="/tools/checkout/front_end/login"]), ' +
                     '[formaction*=checkout], [onclick*=checkout], .additional-checkout-button';

  function findCheckoutControl(target) {
    if (!target || !target.closest) return null;
    try { return target.closest(CHECKOUT_SEL); } catch (e) { return null; }
  }

  function holdThen(resume) {
    holdingCheckout = true;
    var maxMs = Number(window.dtCheckoutHoldMaxMs) || 4000;
    try { document.documentElement.classList.add('dt-checkout-holding'); } catch (e) {}
    whenSettled(maxMs).then(function (settled) {
      if (!settled) log('checkout hold hit the ' + maxMs + 'ms cap — proceeding anyway');
      try { document.documentElement.classList.remove('dt-checkout-holding'); } catch (e) {}
      gateBypass = true;
      try { resume(); } finally { gateBypass = false; holdingCheckout = false; }
    });
  }

  document.addEventListener('click', function (e) {
    if (gateBypass || !dirty) return;
    var btn = findCheckoutControl(e.target);
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (holdingCheckout) return; /* double-click while held — first resume wins */
    log('checkout click while reconciling — holding');
    holdThen(function () { btn.click(); });
  }, true);

  document.addEventListener('submit', function (e) {
    if (gateBypass || !dirty) return;
    var form = e.target;
    var action = (form && form.getAttribute && form.getAttribute('action')) || '';
    var submitter = e.submitter || null;
    /* Gate only checkout submissions: an explicit checkout action, or the
       cart form submitted BY a checkout control (e.submitter covers the
       Enter-key implicit submitter). Plain qty-update submits pass through. */
    if (!(/checkout/.test(action) || (submitter && findCheckoutControl(submitter)))) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (holdingCheckout) return;
    log('checkout submit while reconciling — holding');
    holdThen(function () {
      if (form.requestSubmit) { form.requestSubmit(submitter || undefined); }
      else if (submitter && submitter.click) { submitter.click(); }
      else { form.submit(); }
    });
  }, true);

  /* Subtle busy hint on the primary controls while a checkout is held. */
  (function () {
    try {
      var st = document.createElement('style');
      st.textContent = '.dt-checkout-holding [name=checkout], .dt-checkout-holding .additional-checkout-button { opacity:.65; cursor:progress; }';
      (document.head || document.documentElement).appendChild(st);
    } catch (e) {}
  })();

  /* ── In-cart proof toggle (main-cart-items.liquid renders the row) ──
     Works for BOTH customizers: design-tool lines (ref = _design_serial)
     and Zakeke lines (ref = 'ZK-' + properties.customization). The state
     key is the same on both: properties._artist_proof = 'Yes'/'No'.

     ON : add the proof variant line FIRST (properties._proof_for = ref),
          then stamp _artist_proof on the design line. If step 2 fails the
          proof line still drives the UI (liquid treats proof-line presence
          as ON), so no half-state is customer-visible.
     OFF: remove the proof line(s) FIRST, then stamp _artist_proof = 'No'.
          Same self-healing property if step 2 fails.
     NOTE: /cart/change.js with `properties` REPLACES the property set, so
     we always merge onto the line's current properties (Zakeke's own
     properties must survive). The proof line itself is qty-managed by the
     reconcile rules above; locked rows (low-res forced proof) render as a
     disabled checkbox in liquid and never fire here. */
  var PROOF_VARIANT_ID = 47088548741292;  /* $5 digital proof — same variant CFG.proofVariantId uses.
                                             Aug 28 2026: was 46207629459628 (product variants rebuilt
                                             when the NO-PROOF $0.00 variant 47088548708524 was added) */
  /* Extra-side fee variants — same ones the design tool uses
     (CFG.upchargeStandard / CFG.upchargeValue in design-tool.liquid). */
  var FEE_VARIANT_STANDARD = 41708943278252;  /* $20 — coolers */
  var FEE_VARIANT_VALUE    = 41708903170220;  /* $6  — standard */

  /* v1.2.0 — price(cents) → fee variant id. zakeke-customizer-price v2.0
     publishes window.dtZakekeFeeVariants = [{id, cents}] (the full
     customization-price-1 variant list), so any fee tier Brandon adds to
     that product just works. The list wins on a match; the two hardcoded
     variants remain as backstop when the list is absent or has no match.
     EXACT cents equality only — never guess a charge. */
  function feeVariantForCents(cents) {
    var list = window.dtZakekeFeeVariants;
    if (list && list.length) {
      for (var i = 0; i < list.length; i++) {
        if (list[i] && list[i].id && Number(list[i].cents) === cents) return Number(list[i].id);
      }
    }
    return cents === 2000 ? FEE_VARIANT_STANDARD
         : cents === 600  ? FEE_VARIANT_VALUE : null;
  }

  /* Every variant id that can be a fee line — used by the bare-line guard.
     cart.js item.id is a Number; keep the list numeric. */
  function knownFeeVariantIds() {
    var ids = [FEE_VARIANT_STANDARD, FEE_VARIANT_VALUE];
    var list = window.dtZakekeFeeVariants;
    if (list && list.length) {
      for (var i = 0; i < list.length; i++) {
        if (list[i] && list[i].id) ids.push(Number(list[i].id));
      }
    }
    return ids;
  }
  /* Same note executeCartAdd stamps at add-to-cart — keep the strings identical */
  var PROOF_REQUIRED_NOTE = 'We require proofs on amounts greater than $1000';

  /* Dead properties removed from any cart line that still carries them.
     See the strip pass in reconcile() for why each one is here. */
  var LEGACY_PROPS = ['_configurationVariants', '_dt_fee_added'];

  function setLineProps(key, patch, ref) {
    return getCart().then(function (cart) {
      var line = null;
      for (var i = 0; i < cart.items.length; i++) {
        if (cart.items[i].key === key) { line = cart.items[i]; break; }
      }
      /* Shopify keys are variant:hash(properties) — OUR OWN property patch
         earlier in the same pass (the _proof_required note, or a legacy
         strip) changes the main's key, so ops that captured keys up front must
         re-resolve by the stable ref (_design_serial / ZK-customization). */
      if (!line && ref) {
        for (var j = 0; j < cart.items.length; j++) {
          var q = cart.items[j].properties || {};
          if (q._design_serial === ref || (q.customization && 'ZK-' + q.customization === ref)) {
            line = cart.items[j]; break;
          }
        }
      }
      if (!line) throw new Error('line not found: ' + key);
      var merged = {};
      var src = line.properties || {};
      Object.keys(src).forEach(function (k) { merged[k] = src[k]; });
      /* patch value null = REMOVE the property (change.js replaces the whole
         property set, so omitting a key deletes it) */
      Object.keys(patch).forEach(function (k) {
        if (patch[k] === null) { delete merged[k]; } else { merged[k] = patch[k]; }
      });
      return origFetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        /* line.key, not the key param — the ref fallback may have resolved a
           FRESHER key than the caller captured. */
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: merged })
      }).then(function (r) { if (!r.ok) throw new Error('change.js ' + r.status); return r.json(); });
    });
  }

  function addProofLine(ref) {
    /* Idempotent: if a proof line for this ref already exists, do nothing —
       a double-fire must never create two $5 lines. */
    return getCart().then(function (cart) {
      var exists = cart.items.some(function (it) {
        return (it.properties || {})._proof_for === ref;
      });
      if (exists) { log('proof line already present for', ref); return null; }
      return origFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: PROOF_VARIANT_ID, quantity: 1,
          properties: { _proof_for: ref, _proof_requested_in: 'cart' } }] })
      }).then(function (r) { if (!r.ok) throw new Error('add.js ' + r.status); return r.json(); });
    });
  }

  /* Add a Zakeke extra-side fee line. Idempotent — a double-fire must never
     create two fee lines for the same customization. */
  function addFeeLine(ref, variantId, qty) {
    return getCart().then(function (cart) {
      var exists = cart.items.some(function (it) {
        return (it.properties || {})._upcharge_for === ref;
      });
      if (exists) { log('fee line already present for', ref); return null; }
      return origFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1,
          properties: { _upcharge_for: ref } }] })
      }).then(function (r) { if (!r.ok) throw new Error('add.js ' + r.status); return r.json(); });
    });
  }

  /* v1.4.0: stampFeeMarkerAll() lived here. It wrote _dt_fee_added: 'Yes' on
     every line sharing a ref, as the "customer deleted the fee on purpose,
     never re-add" memory. Zakeke's edit round-trip copies a line's properties
     verbatim onto its replacement, so the stamp survived edits it was never
     meant to survive and permanently suppressed the fee. Deleted, not
     patched — the header explains why no fingerprint variant was used
     instead. _dt_fee_added is now cleaned off existing lines by the strip
     pass. DO NOT reintroduce a cart-line property as an opt-out memory. */

  function removeProofLines(ref) {
    return getCart().then(function (cart) {
      var chain = Promise.resolve();
      cart.items.forEach(function (it) {
        if ((it.properties || {})._proof_for === ref) {
          chain = chain.then(function () { return changeLine(it.key, 0); });
        }
      });
      return chain;
    });
  }

  function refreshCartUi() {
    ['cart:refresh', 'cart:updated', 'cart-drawer:refresh'].forEach(function (ev) {
      document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
    });
    if (/^\/cart\/?$/.test(location.pathname)) window.location.reload();
  }

  document.addEventListener('change', function (e) {
    var box = e.target;
    if (!box || !box.classList || !box.classList.contains('dt-proof-toggle')) return;
    if (box.disabled) return; /* locked (forced proof) — belt and suspenders */
    var key = box.getAttribute('data-line-key');
    var ref = box.getAttribute('data-proof-ref');
    if (!key || !ref) return;
    var wantOn = box.checked;
    var row = box.closest('[data-dt-proof-row]');
    var err = row && row.querySelector('[data-dt-proof-error]');
    if (row) row.classList.add('dt-proof-row--busy');
    if (err) err.style.display = 'none';
    log('proof toggle', wantOn ? 'ON' : 'OFF', 'ref', ref);

    var op = wantOn
      ? addProofLine(ref).then(function () { return setLineProps(key, { _artist_proof: 'Yes', _design_approved: null }, ref); })
      : removeProofLines(ref).then(function () { return setLineProps(key, { _artist_proof: 'No' }, ref); });

    op.then(function () {
      refreshCartUi();
    }).catch(function (ex) {
      log('proof toggle failed', ex);
      box.checked = !wantOn; /* revert */
      if (row) row.classList.remove('dt-proof-row--busy');
      if (err) err.style.display = 'block';
    });
  });

  /* ── Load-time pass ──────────────────────────────────────────────
     Covers non-AJAX cart form posts (full page reload) and anything that
     drifted while this script wasn't loaded. Only runs where a cart can
     realistically be edited — cheap enough to run everywhere, but skip
     obvious non-pages (checkout owns its own line rendering). */
  if (!/^\/checkouts?/.test(location.pathname)) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { scheduleReconcile('pageload'); });
    } else {
      scheduleReconcile('pageload');
    }
    /* #4 (Jul 23 2026): a bfcache restore (e.g. browser back from checkout)
       does NOT refire DOMContentLoaded — pageshow does. Cheap, and a
       reconcile that finds nothing to change is a no-op. */
    window.addEventListener('pageshow', function (e) { if (e.persisted) scheduleReconcile('pageshow'); });
  }
})();